import React, { useEffect, useState } from "react";
import { X, Sparkles, UserCheck } from "lucide-react";

/**
 * v2.2 — In-app Assign dialog. Replaces window.confirm() (which looked like
 * an error pop-up). Shows the suggested user, but lets the IT operator pick
 * any eligible IT teammate from a dropdown before confirming.
 *
 * Props:
 *   open: boolean
 *   onClose(): close without action
 *   onSubmit(userId): called on confirm with the chosen user-id
 *   suggested: { id, full_name, role, open_count, category_familiarity } | null
 *   users: full users list (for the picker)
 *   allowedRoles: array of role strings to filter the picker
 *   ticketCategory: string (shown for context)
 *   ticketNumber: string (shown in title)
 */
export default function AssignDialog({
  open, onClose, onSubmit,
  suggested, users = [], allowedRoles = ["it_team", "it_manager", "it_security", "admin"],
  ticketCategory, ticketNumber,
}) {
  const [selected, setSelected] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setSelected(suggested?.id || "");
  }, [open, suggested?.id]);

  if (!open) return null;

  const eligible = users.filter((u) => u.is_active !== false && allowedRoles.includes(u.role));

  async function submit() {
    if (!selected) return;
    setSubmitting(true);
    try { await onSubmit(selected); }
    finally { setSubmitting(false); }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in"
      onClick={onClose}
    >
      <div className="bg-white rounded-xl shadow-elevated w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-brand-500 to-brand-600 text-white rounded-t-xl">
          <span className="font-semibold flex items-center gap-2">
            <UserCheck size={16} /> Assign ticket {ticketNumber || ""}
          </span>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded transition" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {suggested ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1">
                <Sparkles size={12} /> Workload suggestion
              </div>
              <div className="mt-1 text-sm">
                <span className="font-semibold text-slate-800">{suggested.full_name}</span>
                <span className="text-slate-500"> · open tickets: {suggested.open_count ?? 0}</span>
                {typeof suggested.category_familiarity === "number" && ticketCategory && (
                  <span className="text-slate-500"> · prior {ticketCategory}: {suggested.category_familiarity}</span>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-800">
              No suggestion available. Pick an assignee below.
            </div>
          )}

          <div>
            <label className="label">Assign to</label>
            <select
              className="input"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              autoFocus
            >
              <option value="">— Choose someone —</option>
              {eligible.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name} ({u.role}{u.plant ? ` · ${u.plant}` : ""})
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              The suggestion is based on current workload and category familiarity. You can override it.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 bg-slate-50 rounded-b-xl">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            type="button"
            disabled={!selected || submitting}
            onClick={submit}
            className="px-4 py-2 rounded-lg font-semibold shadow-soft transition bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white disabled:opacity-60"
          >
            {submitting ? "Assigning…" : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
}
